import endpoints from '../endpoints.ts';
import api from '../api/index.ts';
import { MedRequest } from '../api/med.ts';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { Toast } from '../types/models.ts';
import { ORDER_QUERY_KEY } from '../queryHooks/orderQueryHooks.ts';
import { MedAPI } from '../api/med.ts';

export const MED_QUERY_KEY = 'MED_QUERY_KEY';

const useCreateManySendPedigrees = (
  options?: UseMutationOptions<
    Toast | null,
    unknown,
    MedRequest.CreateManySendPedigrees
  >
) =>
  useMutation({
    mutationKey: [endpoints.med.create.many.sendPedigrees()],
    mutationFn: api.med.create.many.sendPedigrees,
    ...options,
  });

const useUpdateOneUnverifyReceipt = (
  options: Partial<
    UseMutationOptions<
      Toast | null,
      unknown,
      { epcisSgtinId: number | string; documentId: number | string }
    >
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [endpoints.med.update.one.unverifyReceipt()],
    mutationFn: api.med.update.one.unverifyReceipt,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORDER_QUERY_KEY],
      });
    },
    ...options,
  });
};

const useUpdateReceiveManyBySerialNumber = (
  options: Partial<
    UseMutationOptions<
      Toast | null,
      unknown,
      MedAPI.Request.UpdateReceiveManyBySerialNumber
    >
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [endpoints.med.update.receive.many.by.serialNumber()],
    mutationFn: api.med.update.receive.many.by.serialNumber,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORDER_QUERY_KEY],
      });
    },
    ...options,
  });
};

const useUpdateReceiveManyByEpcisMedVocabularyId = (
  options: Partial<
    UseMutationOptions<
      Toast | null,
      unknown,
      MedAPI.Request.UpdateRecieveManyByEpcisMedVocabularyId
    >
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [endpoints.med.update.receive.many.by.epcisMedVocabularyId()],
    mutationFn: api.med.update.receive.many.by.epcisMedVocabularyId,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORDER_QUERY_KEY],
      });
    },
    ...options,
  });
};

const useUpdateReceiveManyByShippingContainerCode = (
  options: Partial<
    UseMutationOptions<
      Toast | null,
      unknown,
      MedAPI.Request.UpdateReceiveManyByShippingContainerCode
    >
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [endpoints.med.update.receive.many.by.shippingContainerCode()],
    mutationFn: api.med.update.receive.many.by.shippingContainerCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORDER_QUERY_KEY],
      });
    },
    ...options,
  });
};

const useUpdateReceiveAllByDocumentId = (
  options: Partial<UseMutationOptions<Toast | null, unknown, number | string>>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [endpoints.med.update.receive.all.by.documentId()],
    mutationFn: api.med.update.receive.all.by.documentId,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORDER_QUERY_KEY],
      });
    },
    ...options,
  });
};

const useDeleteOneAttachement = (
  options: Partial<
    UseMutationOptions<
      Toast | null,
      unknown,
      { attachmentId: number | string; isEpcisGenerated: boolean }
    >
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [endpoints.med.delete.one.attachment()],
    mutationFn: api.med.delete.one.attachment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MED_QUERY_KEY],
      });
    },
    ...options,
  });
};

const medMutationHooks = {
  useCreateManySendPedigrees,
  useUpdateOneUnverifyReceipt,
  useUpdateReceiveManyBySerialNumber,
  useUpdateReceiveManyByEpcisMedVocabularyId,
  useUpdateReceiveManyByShippingContainerCode,
  useUpdateReceiveAllByDocumentId,
  useDeleteOneAttachement,
};

export default medMutationHooks;
